<script setup>
import { ref, computed, watch, reactive, onMounted, onUnmounted } from 'vue';
import { Inertia as router } from '@inertiajs/inertia';
import { Link } from '@inertiajs/inertia-vue3';
import Buttons from "@/Components/Buttons.vue";
import SelectSearch from "@/Components/SelectSearch.vue";
import CardBoxModal from "@/Components/CardBoxModal.vue";
import NotificationAlert from "@/Components/NotificationAlert.vue";
import { useTableExport } from '@/Common/useTableExport.js';
import { Icon } from '@iconify/vue';

// Formateadores (isDateColumn, autoFormat) se mantienen igual...
const isDateColumn = (col, value) => {
    if (col?.filterType === 'date' || col?.filterType === 'datetime') return true;
    if (!value) return false;
    const key = col?.key || '';
    // Verifica si la llave termina en _at o si el valor tiene formato de fecha ISO
    const isAtKey = key.toLowerCase().endsWith('_at');
    const isIsoDate = typeof value === 'string' && value.includes('-') && !isNaN(Date.parse(value));
    return isAtKey || isIsoDate;
};

const parseDate = (val) => {
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val.trim())) {
        return new Date(val.trim() + 'T00:00:00');
    }
    return new Date(val);
};

const autoFormat = (val) => {
    const d = parseDate(val);
    return {
        day: d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }),
        hour: d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', hour12: true }).replace(/\./g, '')
    };
};

const props = defineProps({
    columns: { type: Array, required: true },
    data: { type: [Array, Object], required: true },
    title: { type: String, default: '' },
    ruta: { type: String, default: '' },
    modulo: { type: String, default: '' },
    topScrollbar: { type: Boolean, default: false },
    simpleSearch: { type: Boolean, default: false },
    manageColumns: { type: Boolean, default: false },
    checkable: { type: Boolean, default: false },
    exporteable: { type: Boolean, default: false },
    processing: { type: Boolean, default: false }, // Loader overlay prop
    userParam: [Array, Object],
    useFilterAnio: { type: Number, default: 0 },
    extraParams: { type: Object, default: () => ({}) },
    nowrap: { type: Boolean, default: false },
    defaultSort: { type: String, default: '' },
    defaultOrder: { type: String, default: 'asc' }
});

const filterAnio = ref(props.useFilterAnio);
watch(() => props.useFilterAnio, (newVal) => {
    filterAnio.value = newVal;
});

const { exportTableData } = useTableExport();

const exportData = (ext) => {
    exportTableData({
        ext: ext,
        data: sortedData.value, // Exportamos toda la data filtrada y ordenada actualmente visible (sin limitar a la paginación de 20 filas, a menos que el backend de Laravel solo proporcione la página actual)
        columns: visibleColumns.value,
        modulo: props.modulo || 'Reporte',
        titleExport: props.title,
        showIndex: true, // Si quieres numeración
        showTotales: false
    });
};

// Modal de Confirmación Global
const isModalActive = ref(false);
const modalTitle = ref('Confirmar Acción');
const modalMessage = ref('');
let confirmCallback = null;

const openConfirmModal = (message, callback, title = 'Confirmar Acción') => {
    console.log('--- Ejecutando openConfirmModal ---');
    console.log('Mensaje:', message);

    modalMessage.value = message;
    confirmCallback = callback;
    modalTitle.value = title;
    isModalActive.value = true;

    console.log('Estado actual del modal (isModalActive):', isModalActive.value);
};

const executeConfirm = () => {
    if (confirmCallback) confirmCallback();
    isModalActive.value = false;
};

// Configuración de Notificación Flotante
const showNotification = ref(false);
const notificationColor = ref('success');
const notificationMessage = ref('');
let notificationTarget = null;

const displayNotification = (message, color = 'success') => {
    notificationMessage.value = message;
    notificationColor.value = color;
    showNotification.value = true;

    if (notificationTarget) clearTimeout(notificationTarget);
    notificationTarget = setTimeout(() => {
        showNotification.value = false;
    }, 5000);
};

const simpleSearchCol = ref('');
const simpleSearchText = ref('');

const activeSimpleSearchColDef = computed(() => {
    return props.columns.find(c => c.key === simpleSearchCol.value);
});

const hasActiveSimpleSearch = computed(() => {
    return simpleSearchText.value !== '' || Object.keys(filters).some(k => filters[k] !== '');
});

watch(simpleSearchCol, (newVal, oldVal) => {
    if (oldVal && newVal !== oldVal) {
        simpleSearchText.value = '';
    }
});

const applySimpleSearch = () => {
    // Clear all existing filters before applying the single search
    Object.keys(filters).forEach(k => filters[k] = '');
    if (simpleSearchCol.value && simpleSearchText.value !== '') {
        filters[simpleSearchCol.value] = simpleSearchText.value;
    }
    applyFilters();
};

const isServerPaginated = computed(() => {
    return props.data && typeof props.data === 'object' && 'current_page' in props.data && 'data' in props.data;
});

const displayData = computed(() => {
    return isServerPaginated.value ? props.data.data : props.data;
});

const totalItems = computed(() => {
    if (isServerPaginated.value && props.data.total !== undefined) {
        // Si el filtrado cliente redujo la cantidad visible de la página actual,
        // lo reflejamos en el conteo total para que el usuario vea el resultado real mostrado.
        if (filteredData.value && displayData.value && filteredData.value.length < displayData.value.length) {
            return filteredData.value.length;
        }
        return props.data.total;
    } else if (Array.isArray(props.data)) {
        return filteredData.value.length;
    }
    return 0;
});

const currentPage = ref(isServerPaginated.value ? props.data.current_page : 1);
const itemsPerPage = ref(isServerPaginated.value ? parseInt(props.data.per_page) : 20);

const sortKey = ref('');
const sortOrder = ref('asc');

// Ref para scroll superior
const topScroll = ref(null);
const bottomScroll = ref(null);
const tableEl = ref(null);
const scrollWidth = ref(0);
const hasOverflow = ref(false);

const isInternalProcessing = ref(false);
let isScrollingTop = false;
let isScrollingBottom = false;

const syncScroll = (source) => {
    if (!props.topScrollbar || !topScroll.value || !bottomScroll.value) return;

    if (source === 'top') {
        if (isScrollingTop) {
            isScrollingTop = false;
            return;
        }
        isScrollingBottom = true;
        bottomScroll.value.scrollLeft = topScroll.value.scrollLeft;
    } else if (source === 'bottom') {
        if (isScrollingBottom) {
            isScrollingBottom = false;
            return;
        }
        isScrollingTop = true;
        topScroll.value.scrollLeft = bottomScroll.value.scrollLeft;
    }
};

let resizeOb = null;
const updateScrollWidth = () => {
    if (props.topScrollbar && tableEl.value && bottomScroll.value) {
        scrollWidth.value = tableEl.value.scrollWidth;
        hasOverflow.value = tableEl.value.scrollWidth > bottomScroll.value.clientWidth;
    }
};

// Filtros
const filters = reactive({});
const appliedFilters = reactive({});

// --- NUEVA LÓGICA DE INICIALIZACIÓN TEMPRANA (SETUP) ---
const urlParams = new URLSearchParams(window.location.search);
sortKey.value = urlParams.get('sort') || props.defaultSort || '';
sortOrder.value = urlParams.get('order') || (urlParams.get('sort') ? 'asc' : props.defaultOrder) || 'asc';

let needsInitialFetch = false;
const initialParams = {};

props.columns.forEach(col => {
    if (col.key !== 'actions') {
        let val = urlParams.get(col.key);

        // Detección del filtro por defecto si no hay en URL
        if (val === null && col.filterDefault !== undefined) {
            val = col.filterDefault;
            needsInitialFetch = true;
        }

        filters[col.key] = val || '';
        appliedFilters[col.key] = val || '';

        if (val !== null && val !== '') {
            initialParams[col.key] = val;
        }

        // Check for date range params
        if (isDateColumn(col, '2000-01-01')) {
            filters[col.key + '_start'] = urlParams.get(col.key + '_start') || '';
            appliedFilters[col.key + '_start'] = urlParams.get(col.key + '_start') || '';
            filters[col.key + '_end'] = urlParams.get(col.key + '_end') || '';
            appliedFilters[col.key + '_end'] = urlParams.get(col.key + '_end') || '';

            if (filters[col.key + '_start']) initialParams[col.key + '_start'] = filters[col.key + '_start'];
            if (filters[col.key + '_end']) initialParams[col.key + '_end'] = filters[col.key + '_end'];
        }
    }
});

// Otros filtros
//filters['search_anio'] = ''
//appliedFilters['search_anio'] = ''
//console.log(filters)
// -----------------------------------------------------

// Control para ocultar/mostrar columnas
const columnVisibility = reactive({});
const storageKey = computed(() => 'tableColumns_' + (props.ruta || window.location.pathname.replace(/\//g, '_')));

const loadColumnVisibility = () => {
    const stored = localStorage.getItem(storageKey.value);
    if (stored) {
        try {
            const parsed = JSON.parse(stored);
            Object.assign(columnVisibility, parsed);
        } catch (e) {
            console.error('Error parsing stored column visibility');
        }
    }
};

watch(() => props.columns, (newCols) => {
    let hasServerParams = false;

    // 1. Usar preferencias del Servidor (Prioridad alta)
    if (props.userParam && Array.isArray(props.userParam) && props.userParam.length > 0) {
        props.userParam.forEach((param) => {
            if (param.col && param.col !== 'actions') {
                columnVisibility[param.col] = param.check;
                hasServerParams = true;
            }
        });
    }

    // 2. Si no hay parámetros del servidor, usamos LocalStorage (Prioridad baja / Local)
    if (!hasServerParams) {
        loadColumnVisibility();
    }

    let hasChanges = false;
    newCols.forEach(col => {
        if (col.key !== 'actions' && columnVisibility[col.key] === undefined) {
            columnVisibility[col.key] = true; // Todo visible por defecto
            hasChanges = true;
        }
    });

    // Guardar solo si agregamos nuevas columnas que no estaban en storage y no vienen del servidor principal
    if (hasChanges && !hasServerParams) {
        localStorage.setItem(storageKey.value, JSON.stringify(columnVisibility));
    }
}, { immediate: true });

watch(columnVisibility, (newVal) => {
    localStorage.setItem(storageKey.value, JSON.stringify(newVal));
}, { deep: true });

const visibleColumns = computed(() => {
    return props.columns.filter(col => col.key === 'actions' || columnVisibility[col.key]);
});

const showColumnDropdown = ref(false);

const saveUserParamToBackend = () => {
    if (!props.manageColumns) return;

    // Construir nombre del parámetro (e.g., student-list, cobranzas-list)
    let paramKey = (props.ruta || 'default') + '-list';
    if (props.modulo === 'cobranzas') paramKey = 'cobranzas-list';
    else if (props.ruta === 'students' || props.modulo === 'estudiante') paramKey = 'student-list'; // hardcoded adjustment for backward compat

    const visibleKeys = Object.entries(columnVisibility)
        .filter(([key, isVisible]) => isVisible)
        .map(([key]) => key);

    const formCampos = {
        param: paramKey,
        value: visibleKeys.join(',')
    };

    if (window.axios) {
        window.axios.post('/userparam/updateorstore', formCampos).catch(e => console.error(e));
    }
};

const closeColumnDropdown = () => {
    if (showColumnDropdown.value) {
        // Al cerrar el dropdown, enviar el guardado al backend tal como hacía el TableAdvanced original.
        saveUserParamToBackend();
    }
    showColumnDropdown.value = false;
};

onMounted(() => {
    document.addEventListener('click', closeColumnDropdown);

    // GATILLADOR AUTOMÁTICO HACIA EL BACKEND (Si hubo filtros por defecto aplicados)
    if (needsInitialFetch && isServerPaginated.value) {
        const fullParams = {
            ...initialParams,
            per_page: itemsPerPage.value
        };
        if (sortKey.value) {
            fullParams.sort = sortKey.value;
            fullParams.order = sortOrder.value;
        }
        router.get(window.location.pathname, fullParams, { replace: true, preserveState: true, preserveScroll: true });
    }

    if (props.topScrollbar) {
        resizeOb = new ResizeObserver(() => {
            updateScrollWidth();
        });
        if (tableEl.value) resizeOb.observe(tableEl.value);
        if (bottomScroll.value) resizeOb.observe(bottomScroll.value);
        setTimeout(updateScrollWidth, 300);
    }

    if (props.simpleSearch) {
        const defaultCol = props.columns.find(c => c.key !== 'actions')?.key || '';
        simpleSearchCol.value = defaultCol;

        for (const col of props.columns) {
            if (col.key !== 'actions') {
                const val = urlParams.get(col.key);
                if (val) {
                    simpleSearchCol.value = col.key;
                    simpleSearchText.value = val;
                    break;
                }
            }
        }
    }
});

onUnmounted(() => {
    document.removeEventListener('click', closeColumnDropdown);
    if (resizeOb) resizeOb.disconnect();
});

const executeServerRequest = (params = {}) => {
    const fullParams = { ...params, ...props.extraParams };
    router.get(window.location.pathname, fullParams, { 
        preserveState: true, 
        preserveScroll: true,
        onStart: () => { isInternalProcessing.value = true; },
        onFinish: () => { isInternalProcessing.value = false; }
    });
};

const sortBy = (key) => {
    if (key === 'actions') return;

    if (sortKey.value === key) {
        sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
    } else {
        sortKey.value = key;
        sortOrder.value = 'asc';
    }

    if (isServerPaginated.value) {
        applyFilters();
    }
};

const applyFilters = () => {
    Object.assign(appliedFilters, filters);
    if (isServerPaginated.value) {
        currentPage.value = 1;
        const params = { page: 1 };
        if (itemsPerPage.value !== 20) params.per_page = itemsPerPage.value;
        if (sortKey.value) {
            params.sort = sortKey.value;
            params.order = sortOrder.value;
        }
        Object.keys(appliedFilters).forEach(k => {
            if (appliedFilters[k] !== '' && appliedFilters[k] !== null) params[k] = appliedFilters[k];
        });

        if (filterAnio.value) {
            params['search_anio'] = filterAnio.value; // <-- Filtrar año
        }
        
        executeServerRequest(params);
    } else {
        currentPage.value = 1;
    }
};

const clearAllFilters = () => {
    Object.keys(filters).forEach(key => {
        filters[key] = '';
        appliedFilters[key] = '';
    });
    sortKey.value = props.defaultSort || '';
    sortOrder.value = props.defaultOrder || 'asc';
    simpleSearchText.value = '';
    currentPage.value = 1;
    if (isServerPaginated.value) {
        const resetParams = { page: 1, per_page: itemsPerPage.value };
        if (sortKey.value) {
            resetParams.sort = sortKey.value;
            resetParams.order = sortOrder.value;
        }
        executeServerRequest(resetParams);
    } else {
        currentPage.value = 1;
    }
};

const changePage = (page) => {
    if (isServerPaginated.value) {
        const params = { page, per_page: itemsPerPage.value };
        if (sortKey.value) {
            params.sort = sortKey.value;
            params.order = sortOrder.value;
        }
        Object.keys(appliedFilters).forEach(k => {
            if (appliedFilters[k] !== '' && appliedFilters[k] !== null) params[k] = appliedFilters[k];
        });
        executeServerRequest(params);
    } else {
        currentPage.value = page;
    }
};

const getSelectOptions = (col) => {
    // 1. Si la columna especifica sus propias opciones
    if (col.filterOptions) {
        // Asegura que si el label viene vacío '', retorne el valor por defecto de la BD (value)
        return col.filterOptions.map(opt => ({
            value: opt.value,
            label: (opt.label !== undefined && opt.label !== null && opt.label !== '') ? opt.label : opt.value
        }));
    }

    // 2. Si es una columna booleana, siempre devolver las dos opciones posibles
    if (col.isBoolean) {
        return [
            { value: 1, label: getBooleanText(col, 1) },
            { value: 0, label: getBooleanText(col, 0) }
        ];
    }

    // 3. Fallback dinámico sobre los datos actuales
    if (!Array.isArray(displayData.value)) return [];
    const values = displayData.value.map(item => item[col.key]);
    return [...new Set(values)]
        .filter(val => val !== null && val !== undefined && val !== '')
        .sort((a, b) => (a > b ? 1 : -1))
        .map(val => ({ value: val, label: val }));
};

const getOption = (column, value) => {
    if (!column.filterOptions) return null;
    const option = column.filterOptions.find(opt => String(opt.value) === String(value));
    return option || null;
};

const getOptionColorClass = (color) => {
    if (!color) return "";
    const baseClass = "px-2 py-1 rounded-md min-w-[40px] inline-flex justify-center items-center";
    switch (color) {
        case 'green':  return baseClass + " text-white bg-green-600";
        case 'red':    return baseClass + " text-white bg-red-600";
        case 'yellow': return baseClass + " text-white bg-[#FDC700]";
        case 'blue':   return baseClass + " text-white bg-blue-600";
        case 'gray':   return baseClass + " text-white bg-gray-600";
        case 'orange': return baseClass + " text-white bg-orange-600";
        case 'purple': return baseClass + " text-white bg-purple-600";
        case 'pink':   return baseClass + " text-white bg-pink-600";
        case 'cyan':   return baseClass + " text-white bg-cyan-600";
        case 'lime':   return baseClass + " text-white bg-lime-600";
        case 'teal':   return baseClass + " text-white bg-teal-600";
        case 'indigo': return baseClass + " text-white bg-indigo-600";
        case 'fuchsia':return baseClass + " text-white bg-fuchsia-600";
        case 'rose':   return baseClass + " text-white bg-rose-600";
        case 'slate':  return baseClass + " text-white bg-slate-600";
        case 'stone':  return baseClass + " text-white bg-stone-600";
        case 'neutral':return baseClass + " text-white bg-neutral-600";
        case 'emerald':return baseClass + " text-white bg-emerald-600";
        case 'sky':    return baseClass + " text-white bg-sky-600";
        case 'violet': return baseClass + " text-white bg-violet-600";
        case 'amber':  return baseClass + " text-white bg-amber-600";
        default: return "";
    }
};

// Busca el color definido en col.filterColor: [{ value, color }]
// Útil cuando se usa mapOptionsLocal (sin color) y se quiere colorear por separado.
const getColorForValue = (col, value) => {
    if (!col.filterColor || !Array.isArray(col.filterColor)) return null;
    const entry = col.filterColor.find(fc => String(fc.value) === String(value));
    return entry?.color ?? null;
};

const filteredData = computed(() => {
    // Si no hay datos, retornamos vacio
    if (!displayData.value || !Array.isArray(displayData.value)) return displayData.value || [];

    // Aplicar filtrado cliente sobre los datos actuales. 
    // Esto es vital para el "primer renderizado" antes de que el servidor responda con el filtro aplicado.
    return displayData.value.filter(item => {
        return Object.keys(props.columns).every(colIndex => {
            const col = props.columns[colIndex];
            const key = col.key;
            const cellValue = item[key];

            if (isDateColumn(col, cellValue)) {
                const start = appliedFilters[key + '_start'];
                const end = appliedFilters[key + '_end'];

                if (!start && !end) return true;

                const d = parseDate(cellValue);
                if (isNaN(d.getTime())) return false;
                
                // Extraer año, mes, día en local para comparar con el formato YYYY-MM-DD del input
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                const cellDateStr = `${year}-${month}-${day}`;

                if (start && !end) return cellDateStr >= start;
                if (start && end) return cellDateStr >= start && cellDateStr <= end;
                if (!start && end) return cellDateStr <= end;
            }

            const query = appliedFilters[key];
            if (query === "" || query === null || query === undefined) return true;

            // Handle boolean columns specifically so '1'/1/true and '0'/0/false match properly
            if (col.isBoolean) {
                const queryBool = (query === '1' || query === 1 || query === true || query === 'true');
                const cellBool = (cellValue === 1 || cellValue === '1' || cellValue === true || cellValue === 'true');
                return queryBool === cellBool;
            }

            // Resolve label ONLY if it's NOT a select-type filter (to preserve ID-based filterDefault)
            let compareValue = cellValue;
            const isStrictSelect = col.filterType === 'select' || col.filterType === 'select-search';
            
            if (col.filterOptions && !isStrictSelect) {
                const opt = col.filterOptions.find(o => String(o.value) === String(cellValue));
                compareValue = opt ? (opt.label ?? cellValue) : cellValue;
            }

            return String(compareValue ?? '').toLowerCase().includes(String(query).toLowerCase());
        });
    });
});

const totalPages = computed(() => {
    if (isServerPaginated.value) return props.data.last_page || 1;
    return Math.ceil(filteredData.value.length / itemsPerPage.value) || 1;
});

const sortedData = computed(() => {
    let data = filteredData.value;

    if (isServerPaginated.value || !sortKey.value) return data;

    return [...data].sort((a, b) => {
        let valA = a[sortKey.value];
        let valB = b[sortKey.value];

        const sortCol = props.columns.find(c => c.key === sortKey.value);

        // Manejar fechas
        if (isDateColumn(sortCol, valA) || isDateColumn(sortCol, valB)) {
            const dA = valA ? parseDate(valA).getTime() : 0;
            const dB = valB ? parseDate(valB).getTime() : 0;
            return sortOrder.value === 'asc' ? dA - dB : dB - dA;
        }

        if (valA === null || valA === undefined) valA = '';
        if (valB === null || valB === undefined) valB = '';

        if (typeof valA === 'string' && typeof valB === 'string') {
            return sortOrder.value === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        }
        return sortOrder.value === 'asc' ? (valA > valB ? 1 : -1) : (valB > valA ? 1 : -1);
    });
});

const paginatedData = computed(() => {
    if (isServerPaginated.value) return filteredData.value;
    const start = (currentPage.value - 1) * itemsPerPage.value;
    return sortedData.value.slice(start, start + itemsPerPage.value);
});

const totalDisplayRecords = computed(() => {
    if (isServerPaginated.value) {
        // Consistencia con totalItems: si hay filtrado cliente sobre el set actual, mostrar ese conteo.
        if (filteredData.value && displayData.value && filteredData.value.length < displayData.value.length) {
            return filteredData.value.length;
        }
        return props.data.total;
    }
    return filteredData.value.length;
});

// Watchers
watch(() => props.data, (newData) => {
    if (newData && typeof newData === 'object' && 'current_page' in newData) {
        currentPage.value = newData.current_page;
        itemsPerPage.value = parseInt(newData.per_page);
    } else if (Array.isArray(newData)) {
        currentPage.value = 1;
    }
    if (props.topScrollbar) {
        setTimeout(updateScrollWidth, 100);
    }
}, { deep: true });

watch(itemsPerPage, (newVal, oldVal) => {
    if (newVal === oldVal) return;
    if (isServerPaginated.value) {
        const params = { per_page: newVal };
        if (sortKey.value) {
            params.sort = sortKey.value;
            params.order = sortOrder.value;
        }
        Object.keys(appliedFilters).forEach(k => {
            if (appliedFilters[k] !== '' && appliedFilters[k] !== null) params[k] = appliedFilters[k];
        });
        executeServerRequest(params);
    } else {
        currentPage.value = 1;
    }
});

const selectedItems = ref([]);
const selectAll = ref(false);

const toggleSelectAll = () => {
    if (selectAll.value) {
        selectedItems.value = [...paginatedData.value];
    } else {
        selectedItems.value = [];
    }
};

const isItemSelected = (item) => {
    return selectedItems.value.some(i => i === item);
};

const toggleSelectItem = (item) => {
    const index = selectedItems.value.indexOf(item);
    if (index > -1) {
        selectedItems.value.splice(index, 1);
    } else {
        selectedItems.value.push(item);
    }
    selectAll.value = paginatedData.value.length > 0 && selectedItems.value.length === paginatedData.value.length;
};

const clearSelection = () => {
    selectedItems.value = [];
    selectAll.value = false;
};

// Limpiar la selección si cambian los datos de la página
watch(paginatedData, () => {
    clearSelection();
});


const getBooleanText = (column, value) => {
    // Si la columna tiene etiquetas personalizadas, las usamos
    if (column.booleanLabels) {
        return value === 1 ? column.booleanLabels.true : column.booleanLabels.false;
    }

    // Lógica por defecto mejorada
    const activeKeys = ['status', 'active', 'estado', 'visible'];
    if (activeKeys.includes(column.key)) {
        return value === 1 ? 'ACTIVO' : 'INACTIVO';
    }

    return value === 1 ? 'SI' : 'NO';
};

</script>
<template>
    <!-- LISTA DE OPCIONES PARA EL LLAMADO DE CONTENIDO EN LA TABLA
    • Para determinar el titulo de la columna, ejemplo label: 'Mi titulo'.
    • Para establecer alineación del contenido usar el atributo, por ejemplo; align: 'center'
    • Para habilitar un filtro tipo select; filterType: 'select',
    • Si el contenido a mostrar ese select y booleano; isBoolean: true . Y establecer las etiquetas por ejemplo; booleanLabels: { true: 'Activo', false: 'Inactivo' },
    • Si se desea establecer un filtro select con varias opciones personalizadas; filterType: 'select', filterOptions: [{ value: 1, label: 'Maestro' }, { value: 2, label: 'Administrador' }, { value: 3, label: 'Directivo' }],
    • Si es requerido el contenido a mostrar tenga color, establecer en filter option el atributo de color, ejemplo; { value: 0, label: 'pendiente', color: 'red' }

    • Valor por defecto en select, como ejemplo; filterDefault: '1'
    • Si se requiere usar Select con buscado, establecer el atributo filterType: 'select-search'

    • COMPONENTE TABLEFILTER, OPCIONES ADICIONALES
    • Para desactivar el filtro de cada columna y activar un filtro general; simple-search = true
    • Para activar la barra de desplazamiento superior (Habran 2 barras, arriba y abajo); top-scrollbar = true -->

    <!-- EJEMPLO DE USO -->
    <!-- <TableFilter
        :data="collaborators"
        :columns="columns"
        :simple-search="true"
        :top-scrollbar="true"
        title="Colaboradores"
    /> -->

    <div class="flex flex-col h-full gap-4">
        <div class="flex justify-between flex-col lg:flex-row  items-center p-1 gap-3">
            <div class="w-full lg:w-auto flex justify-start items-center gap-2">
                <h2 v-if="title" class="text-lg font-semibold text-gray-800">
                    {{ title }}<span v-if="totalDisplayRecords > 0"> : {{ totalDisplayRecords }}</span>
                </h2>
            </div>
            <div class="flex flex-col lg:flex-row items-center justify-between gap-3 w-full lg:w-auto">
                <div v-if="exporteable" class="flex items-center justify-center lg:justify-start gap-2 w-full lg:w-auto">
                    <button @click="exportData('pdf')" class="w-9 h-9 rounded-full bg-gray-50 hover:bg-gray-100 border border-gray-200 flex items-center justify-center transition-colors shadow-sm" title="Descargar PDF">
                        <Icon icon="fa6-solid:file-pdf" class="text-[#ab2121] text-lg" />
                    </button>
                    <button @click="exportData('xlsx')" class="w-9 h-9 rounded-full bg-gray-50 hover:bg-gray-100 border border-gray-200 flex items-center justify-center transition-colors shadow-sm" title="Descargar Excel">
                        <Icon icon="fa6-solid:file-excel" class="text-[#1c7847] text-lg" />
                    </button>
                </div>
                <div v-if="useFilterAnio > 0" class="flex items-center gap-1.5 bg-white dark:bg-slate-800 px-2.5 py-1.5 rounded-md border border-gray-300 dark:border-slate-700 shadow-sm">
                    <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <Icon icon="tabler:calendar" class="w-3.5 h-3.5 text-gray-400" />
                        Año:
                    </label>
                    <input
                        type="text"
                        v-model="filterAnio"
                        @change="applyFilters"
                        @keydown.enter.prevent="applyFilters"
                        class="text-xs py-0.5 px-1.5 w-16 text-center rounded border-gray-200 dark:border-slate-600 dark:bg-slate-900 dark:text-white font-bold focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                        title="Filtrar por año"
                    />
                </div>
                <div class="flex flex-row items-center justify-between lg:justify-start gap-2 w-full lg:w-auto">
                    <div class="flex justify-between items-center">
                        <div class="flex items-center gap-1 sm:gap-2 bg-white px-1.5 py-1.5 rounded-md border border-gray-300 shadow-sm">
                            <label class="text-xs font-medium text-gray-500">Ver:</label>
                            <select v-model="itemsPerPage" class="text-xs py-1 pl-1 pr-6 sm:hidden">
                                <option :value="10">10 reg.</option>
                                <option :value="20">20 reg.</option>
                                <option :value="50">50 reg.</option>
                                <option :value="100">100 reg.</option>
                                <option :value="250">250 reg.</option>
                                <option :value="500">500 reg.</option>
                                <option :value="1000">1000 reg.</option>
                                <option :value="2000">2000 reg.</option>
                            </select>
                            <select v-model="itemsPerPage" class="text-xs py-1 pl-1 pr-8 hidden sm:block">
                                <option :value="10">10 registros</option>
                                <option :value="20">20 registros</option>
                                <option :value="50">50 registros</option>
                                <option :value="100">100 registros</option>
                                <option :value="250">250 registros</option>
                                <option :value="500">500 registros</option>
                                <option :value="1000">1000 registros</option>
                                <option :value="2000">2000 registros</option>
                            </select>
                        </div>
                    </div>
                    <div class="flex justify-between items-center">
                        <div class="flex items-center gap-1 sm:gap-2 bg-white pl-1.5 pr-2 sm:pr-3 py-1.5 rounded-md border border-gray-300 shadow-sm">
                            <select v-model="currentPage" @change="changePage(currentPage)" class="text-xs py-1 pl-1 pr-6 sm:hidden">
                                <option v-for="n in totalPages" :key="n" :value="n" class="pr-3">
                                    Pág. {{ n }}
                                </option>
                            </select>
                            <select v-model="currentPage" @change="changePage(currentPage)" class="text-xs py-1 pl-1 pr-8 hidden sm:block">
                                <option v-for="n in totalPages" :key="n" :value="n" class="pr-3">
                                    Página {{ n }}
                                </option>
                            </select>
                            <span class="text-xs text-gray-400">de {{ totalPages }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Simple Search Block -->
        <div v-if="simpleSearch" class="grid grid-cols-[1fr_auto] sm:flex sm:flex-row items-center gap-2 w-full md:w-max mb-1">

            <select v-model="simpleSearchCol" class="w-full sm:w-auto order-1">
                <template v-for="col in columns" :key="'ss-' + col.key">
                    <option v-if="col.key !== 'actions'" :value="col.key">{{ col.label }}</option>
                </template>
            </select>

            <Buttons @click="applySimpleSearch" color="blue" title="Buscar" class="order-2 sm:order-3">
                <Icon icon="fa6-solid:magnifying-glass" />
            </Buttons>

            <div class="block w-full sm:w-[300px] order-3 sm:order-2" :class="!hasActiveSimpleSearch ? 'col-span-2 sm:col-span-1' : ''">
                <template v-if="activeSimpleSearchColDef?.filterType === 'select'">
                    <select v-model="simpleSearchText" class="w-full" @change="applySimpleSearch">
                        <option value="">Todos</option>
                        <option v-for="opt in getSelectOptions(activeSimpleSearchColDef)" :key="opt.value" :value="opt.value">
                            {{ opt.label }}
                        </option>
                    </select>
                </template>
                <template v-else-if="activeSimpleSearchColDef?.filterType === 'select-search'">
                    <SelectSearch v-model="simpleSearchText" :options="getSelectOptions(activeSimpleSearchColDef)" placeholder="Todos" @change="applySimpleSearch" />
                </template>
                <template v-else>
                    <input v-model="simpleSearchText" type="text" class="w-full" placeholder="Buscar..." @keyup.enter="applySimpleSearch" />
                </template>
            </div>

            <Buttons v-if="hasActiveSimpleSearch" @click="clearAllFilters" color="red" title="Limpiar búsqueda" class="order-4">
                <Icon icon="fa6-solid:broom" />
            </Buttons>
        </div>

        <div v-if="selectedItems.length > 0" class="border p-3 rounded-md flex flex-col xl:flex-row items-center gap-4 mb-1 transition-all">
            <div class="font-semibold text-sm flex items-center gap-2 whitespace-nowrap">
                <Icon icon="fa6-solid:list-check" />
                <span>{{ selectedItems.length }} filas seleccionadas</span>
            </div>
            <div class="flex items-center justify-end gap-3 w-full xl:w-auto">
                <slot name="mass-actions" :selectedItems="selectedItems" :clearSelection="clearSelection" :requestConfirm="openConfirmModal" :notify="displayNotification"></slot>
            </div>
        </div>

        <div ref="tableContainer" class="relative sm:rounded-md flex flex-col bg-white border border-gray-200 shadow-sm">
            <!-- Barra de desplazamiento superior (opcional) -->
            <div v-if="topScrollbar" v-show="hasOverflow" ref="topScroll" class="overflow-x-auto w-full custom-scrollbar-top border-b" @scroll="syncScroll('top')">
                <div :style="`width: ${scrollWidth}px;`" class="h-[4px] lg:h-[1px]"></div>
            </div>
            <div class="overflow-x-auto min-h-[450px]" ref="bottomScroll" @scroll="syncScroll('bottom')">
                <table ref="tableEl" class="text-sm text-left rtl:text-right text-gray-700 table-auto min-w-full rounded-md">
                    <thead class="text-sm text-gray-900 bg-gray-100 border-b sticky top-0 z-10">
                        <tr v-if="!simpleSearch" class="bg-gray-50/50">
                            <th v-for="(col, colIdx) in visibleColumns" :key="'filter-' + col.key" class="px-2 py-2 border-b border-gray-200" :class="{ 'border-l border-gray-300/50': colIdx !== 0 }">
                                <div v-if="col.key === 'actions'" class="flex justify-center gap-2">
                                    <Buttons @click="applyFilters" size="iconSm" title="Buscar" color="blue">
                                        <Icon icon="fa6-solid:magnifying-glass" />
                                    </Buttons>
                                    <Buttons @click="clearAllFilters" size="iconSm" title="Limpiar filtros" color="red">
                                        <Icon icon="boxicons:broom-filled" />
                                    </Buttons>
                                </div>
                                <div v-else class="flex justify-center">
                                    <!-- <select v-if="col.filterType === 'select'" v-model="filters[col.key]" class="w-full min-w-0" v-on:keyup.enter="applyFilters">
                                        <option value="">Todos</option>
                                        <option v-for="val in getUniqueValues(col.key)" :key="val" :value="val">
                                            {{ col.isBoolean ? (val === 1 ? 'SI' : 'NO') : val }}
                                        </option>
                                    </select> -->
                                    <select v-if="col.filterType === 'select'" v-model="filters[col.key]" class="w-full min-w-[106px]" @change="applyFilters" @keyup.enter="applyFilters">
                                        <option value="">Todos</option>
                                        <option v-for="opt in getSelectOptions(col)" :key="opt.value" :value="opt.value">
                                            {{ opt.label }}
                                        </option>
                                    </select>
                                    <div v-else-if="col.filterType === 'select-search'" class="w-full min-w-[150px]">
                                        <SelectSearch v-model="filters[col.key]" :options="getSelectOptions(col)" placeholder="Buscar" @change="applyFilters" />
                                    </div>
                                    <div v-else-if="isDateColumn(col, displayData[0]?.[col.key])" class="flex flex-col gap-1 w-full">
                                        <input v-model="filters[col.key + '_start']" type="date" class="w-full min-w-0 font-normal text-center" placeholder="Desde" v-on:keyup.enter="applyFilters" />
                                        <input v-model="filters[col.key + '_end']" type="date" class="w-full min-w-0 font-normal text-center" placeholder="Hasta" v-on:keyup.enter="applyFilters" />
                                    </div>
                                    <input v-else v-model="filters[col.key]" type="text" class="w-full min-w-0 font-normal text-gray-700 placeholder-gray-400" placeholder="" v-on:keyup.enter="applyFilters" title="Filtrar..." />
                                </div>
                            </th>
                        </tr>
                        <tr>
                            <th v-for="(col, colIdx) in visibleColumns" :key="col.key" class="px-4 py-3 text-center font-bold uppercase tracking-wider whitespace-nowrap" :class="[
                                { 'border-l border-gray-300/50': colIdx !== 0 },
                                col.key !== 'actions' ? 'cursor-pointer select-none hover:bg-gray-200 transition-colors' : ''
                            ]" :style="col.width ? { width: col.width, minWidth: col.width } : { width: '1%' }" @click="col.key !== 'actions' ? sortBy(col.key) : null">

                                <div v-if="col.key === 'actions'" class="relative flex justify-center items-center gap-2" @click.stop>
                                    <input v-if="checkable" type="checkbox" v-model="selectAll" @change="toggleSelectAll" class="w-4 h-4 cursor-pointer text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-1" title="Seleccionar todos">
                                    <template v-if="manageColumns">
                                        <div class="relative">
                                            <Buttons @click="showColumnDropdown = !showColumnDropdown" color="blue" size="iconSm" title="Personalizar Columnas">
                                                <Icon icon="fa6-solid:list-ul" />
                                            </Buttons>

                                            <!-- Menú de opciones de columnas -->
                                            <div v-show="showColumnDropdown" class="absolute left-0 mt-2 top-full bg-white border border-gray-200 shadow-xl rounded-md pt-2 pb-0 z-50 text-left min-w-[240px] cursor-default flex flex-col">
                                                <div class="font-bold text-gray-800 mb-2 px-3 border-b flex flex-col pb-2 shrink-0 text-left">
                                                    <span class="text-[13px] capitalize truncate pb-0 block text-left w-full h-auto" style="text-align: left; text-transform:none">Configuración</span>
                                                    <span class="text-[11px] font-normal text-gray-400 normal-case block text-left" style="text-align: left;">Gestionar visibilidad de columnas</span>
                                                </div>
                                                <div class="flex flex-col gap-0 overflow-y-auto w-full custom-scrollbar max-h-[250px] pb-2 px-1">
                                                    <label v-for="c in columns.filter(c => c.key !== 'actions')" :key="'toggle-' + c.key" class="flex items-center gap-3 px-2 py-1.5 hover:bg-blue-50/50 rounded-md cursor-pointer transition-colors text-sm normal-case font-normal text-gray-700 bg-white group select-none">
                                                        <div class="relative flex items-center justify-center shrink-0">
                                                            <input type="checkbox" v-model="columnVisibility[c.key]" class="peer appearance-none w-4 h-4 border border-gray-300 rounded cursor-pointer checked:bg-blue-600 checked:border-blue-600 focus:ring-2 focus:ring-blue-500/30 transition-all shadow-sm" @click.stop/>
                                                            <Icon icon="fa6-solid:check" class="absolute text-white text-[10px] opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity" />
                                                        </div>
                                                        <span class="truncate block w-full text-left group-hover:text-blue-800 transition-colors" style="text-align: left;">{{ c.label }}</span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                    <template v-else>
                                        <span>{{ col.label }}</span>
                                    </template>
                                </div>
                                <div v-else class="flex items-center justify-center gap-2">
                                    <span>{{ col.label }}</span>
                                    <div class="flex flex-col text-[10px] items-center -space-y-[4px]">
                                        <Icon icon="fa6-solid:sort-up" :class="(sortKey === col.key && sortOrder === 'asc') ? 'text-blue-600' : 'text-gray-300 text-opacity-70'" />
                                        <Icon icon="fa6-solid:sort-down" :class="(sortKey === col.key && sortOrder === 'desc') ? 'text-blue-600' : 'text-gray-300 text-opacity-70'" />
                                    </div>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tr v-if="isInternalProcessing">
                        <td :colspan="visibleColumns.length">
                            <div class="bg-white">
                            </div>
                        </td>
                    </tr>
                    <tr v-else-if="paginatedData.length === 0">
                        <td :colspan="visibleColumns.length" class="px-4 py-12 text-center">
                            <div class="flex flex-col items-center justify-center text-gray-400">
                                <Icon icon="fa6-solid:magnifying-glass" class="text-4xl mb-3 opacity-20" />
                                <p class="text-lg font-medium">No se encontraron registros</p>
                                <p class="text-sm">Intenta ajustar los filtros de búsqueda</p>
                                <Buttons @click="clearAllFilters" class="mt-4" color="blue" size="sm">
                                    Limpiar todos los filtros ahora
                                </Buttons>
                            </div>
                        </td>
                    </tr>
                    <tr v-else v-for="(item, index) in paginatedData" :key="index" class="border-b transition-colors hover:bg-gray-100 h-[60px]" :class="[index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50']">
                        <td v-for="(col, colIdx) in visibleColumns" :key="col.key" class="px-4 py-3" :class="[colIdx === 0 ? 'font-medium text-gray-700' : 'text-gray-600', { 'border-l border-gray-100': colIdx !== 0, 'whitespace-nowrap': nowrap }]" :style="col.width ? { width: col.width, minWidth: col.width } : {}">
                            <div class="max-w-[400px] text-ellipsis w-full flex items-center p-1 gap-2" :class="[col.key !== 'actions' ? 'overflow-hidden' : '', col.align === 'center' ? 'justify-center text-center' : '', col.align === 'right' ? 'justify-end text-right' : col.align === 'between' ? 'justify-between' : 'justify-start text-left']">
                                <input v-if="checkable && col.key === 'actions'" type="checkbox" :checked="isItemSelected(item)" @change="toggleSelectItem(item)" class="w-4 h-4 cursor-pointer text-blue-600 border-gray-300 rounded focus:ring-blue-500 shrink-0" title="Seleccionar">
                                <slot :name="'cell-' + col.key" :item="item">
                                    <template v-if="item[col.key] && isDateColumn(col, item[col.key])">
                                        <div class="flex flex-col leading-tight items-center justify-center">
                                            <span class="font-semibold text-gray-700">{{ autoFormat(item[col.key]).day }}</span>
                                            <span v-if="col.fieldType == 'datetime'" class="text-[10px] text-gray-400 uppercase tracking-widest">{{ autoFormat(item[col.key]).hour }}</span>
                                        </div>
                                    </template>
                                    <!-- <template v-else-if="col.isBoolean">
                                        <div class="font-bold px-2 py-1 rounded-md w-[38px] flex justify-center items-center" :class="item[col.key] === 1 ? 'text-green-100 bg-green-600 ' : 'text-red-100 bg-red-600 '">
                                            {{ item[col.key] === 1 ? 'SI' : 'NO' }}
                                        </div>
                                    </template> -->
                                    <template v-else-if="col.isBoolean">
                                        <div class="px-2 py-1 rounded-md min-w-[40px] flex justify-center items-center" :class="item[col.key] === 1 ? 'text-green-100 bg-green-600' : 'text-red-100 bg-red-600'">
                                            {{ getBooleanText(col, item[col.key]) }}
                                        </div>
                                    </template>
                                    <!-- <template v-else-if="(col.filterType == 'select' || col.filterType == 'select-search')"> -->
                                    <template v-else-if="col.filterOptions">
                                        <div v-if="getOption(col, item[col.key])?.color || getColorForValue(col, item[col.key])" :class="getOptionColorClass(getOption(col, item[col.key])?.color || getColorForValue(col, item[col.key]))">
                                            {{ getOption(col, item[col.key])?.label ?? '' }}
                                        </div>
                                        <span v-else>
                                            {{ getOption(col, item[col.key])?.label ?? '' }}
                                        </span>
                                    </template>
                                    <template v-else-if="col.linkObject">
                                        <Link :href="col.linkObject.replace('__ID__', item.id)" class="text-blue-600 hover:text-blue-800 hover:font-semibold transition-all ease-in-out delay-150 duration-150">
                                            {{ item[col.key] }}
                                        </Link>
                                    </template>
                                    <template v-else-if="col.fieldType == 'mobile_phone'">
                                        {{ item[col.key] }}
                                        <div v-if="item[col.key]">
                                            <Buttons :href="'https://wa.me/51' + (item[col.key] ?? '') + '?text=Hola'" target="_blank" color="green" size="iconSm">
                                                <Icon icon="fa6-brands:whatsapp" />
                                            </Buttons>
                                        </div>
                                    </template>
                                    <template v-else>
                                        {{ item[col.key] }}
                                    </template>
                                </slot>

                            </div>
                        </td>
                    </tr>
                </table>
            </div>
            <div v-if="paginatedData.length > 0" class="flex flex-col sm:flex-row items-center justify-between p-4 bg-white rounded-b-md gap-3 border-t">
                <div class="text-xs text-gray-500">
                    Mostrando <span class="font-semibold text-gray-700">{{ paginatedData.length }}</span> de <span class="font-semibold text-gray-700">{{ totalDisplayRecords }}</span> registros
                </div>
                <div class="flex items-center gap-4">
                    <span class="text-xs text-gray-500 whitespace-nowrap">Página {{ currentPage }} de {{ totalPages }}</span>
                    <div class="inline-flex shadow-sm rounded-md">
                        <button @click="changePage(currentPage - 1)" :disabled="currentPage === 1" class="px-4 py-2 text-sm font-medium border rounded-l-lg transition-colors border-gray-300" :class="[currentPage === 1 ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50']">
                            Ant.
                        </button>
                        <button @click="changePage(currentPage + 1)" :disabled="currentPage === totalPages" class="px-4 py-2 text-sm font-medium border border-l-0 rounded-r-lg transition-colors border-gray-300" :class="[currentPage === totalPages ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50']">
                            Sig.
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <Teleport to="body">
            <CardBoxModal v-model="isModalActive" :title="modalTitle" button="danger" hasCancel @confirm="executeConfirm">
                <p class="text-sm whitespace-pre-line" v-html="modalMessage"></p>
            </CardBoxModal>

            <!-- Overlay Loading Screen (Procesando) genérico -->
            <div v-if="processing || isInternalProcessing" class="fixed container-snap h-full w-full z-[100] left-0 top-0 overflow-hidden">
                <div class="flex flex-col items-center justify-center w-full h-full bg-white bg-opacity-80">
                    <div class="border-gray-200 h-20 w-20 animate-spin rounded-full border-8 border-t-rose-600 mb-2 shadow-sm"></div>
                    <p class="text-rose-600 font-semibold tracking-wide">Procesando...</p>
                </div>
            </div>

            <div class="fixed top-[90px] right-0 w-full z-[100] pointer-events-none">
                <div class="pointer-events-auto">
                    <NotificationAlert v-model="showNotification" :color="notificationColor" :message="notificationMessage" />
                </div>
            </div>
        </Teleport>
    </div>
</template>
